import { createFileRoute, useLoaderData } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { FillScreen } from "@/components/Layout";
import { defaultNS } from "@/lib/i18n";

import { AppsAlertHelpText as Help } from "../../-components/apps-alerts-help-text";
import {
  AppsAlertTabs,
  useAppsAlertTabs,
} from "../../-components/apps-alerts-tabs";
import { AppsAlertType } from "../../-zod-schema";

import { AppsAlertsNoWorkerFilter as Filter } from "./-components/filter";
import { AppsAlertsNoWorkerTable as Table } from "./-components/table";
import { useAppsAlertsNoWorker, useExport } from "./-query";
import { type SearchParams, SearchSchema } from "./-zod-schema";

export const Route = createFileRoute(
  "/_staff/apps/_list/alerts/_alerts/no-worker/",
)({
  validateSearch: zodValidator(SearchSchema),
  component: AppsAlertsNoWorker,
});

function AppsAlertsNoWorker() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.apps.alert",
  });

  // 共通系
  const counts = useLoaderData({ from: Route.parentRoute.id });
  const { onChangeTab, emptyCounts } = useAppsAlertTabs();

  // ここからNoWorkerの話
  const { data, isPending } = useAppsAlertsNoWorker(search);

  const { mutate: onExport, isPending: exporting } = useExport(search);

  const onSearch = useCallback(
    (query: Partial<SearchParams>) => {
      navigate({ search: (prev) => ({ ...prev, ...query }) });
    },
    [navigate],
  );

  const onChangeFilter = useCallback(
    // 検索条件を変更したら1ページ目から検索する
    (query: Partial<SearchParams>) => onSearch({ ...query, pageIndex: 0 }),
    [onSearch],
  );

  return (
    <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <AppsAlertTabs
        value={AppsAlertType.noWorker.key}
        onChange={onChangeTab}
        counts={counts ?? emptyCounts}
      />
      <Help
        title={t("help.unidentified.title")}
        text={t("help.unidentified.text")}
      />
      <Filter
        state={search}
        onChange={onChangeFilter}
        exporting={exporting}
        onClickExport={onExport}
      />
      <Table
        data={data ?? { items: [], count: 0, maxPage: 0, page: 1, length: 0 }}
        search={search}
        onSearch={onSearch}
        isPending={isPending}
      />
    </FillScreen>
  );
}
